import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Drawer,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconPlus } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useJournal, useJournalEntry } from './journalApi';
import { ManualEntryDrawer } from './ManualEntryDrawer';
import { JOURNAL_SOURCE, type JournalEntryDto } from './types';

// Phase Accounting-6: دفتر اليومية — paged ledger of every journal
// entry with source filter and a lines drawer per entry.

export function JournalPage() {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [source, setSource] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading } = useJournal({
    from: range[0] ? toIsoDate(range[0]) : null,
    to: range[1] ? toIsoDate(range[1]) : null,
    source: source ? Number(source) : null,
    page,
  });
  const detail = useJournalEntry(selectedId);

  const sourceOptions = Object.entries(JOURNAL_SOURCE).map(([, v]) => ({
    value: String(v),
    label: t(sourceLabel(v)),
  }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.journal.title')}
        subtitle={t('accounting.journal.subtitle')}
      />

      <Paper p="md">
        <Group justify="space-between" wrap="wrap">
          <Group>
            <DatePickerInput
              type="range"
              value={range}
              onChange={setRange}
              placeholder={t('accounting.journal.rangePlaceholder')}
              clearable
              style={{ minWidth: 260 }}
            />
            <Select
              data={sourceOptions}
              value={source}
              onChange={setSource}
              placeholder={t('accounting.journal.sourcePlaceholder')}
              clearable
              style={{ minWidth: 180 }}
            />
          </Group>
          <Button leftSection={<IconPlus size={16} stroke={1.6} />} onClick={() => setManualOpen(true)}>
            {t('accounting.journal.manual.title')}
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <Paper p={0}>
          <Table verticalSpacing="sm" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.journal.columns.number')}</Table.Th>
                <Table.Th>{t('accounting.journal.columns.date')}</Table.Th>
                <Table.Th>{t('accounting.journal.columns.source')}</Table.Th>
                <Table.Th>{t('accounting.journal.columns.description')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.amount')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(data?.rows ?? []).map((e) => (
                <Table.Tr
                  key={e.journalEntryId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(e.journalEntryId)}
                >
                  <Table.Td className="font-numeric">{e.entryNumber}</Table.Td>
                  <Table.Td className="font-numeric">{e.entryDate}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" radius="sm">
                      {t(sourceLabel(e.source))}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" lineClamp={1}>{e.description ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    {e.totalDebit.toFixed(2)}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data && data.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text size="sm" c="dimmed" ta="center" py="lg">
                      {t('accounting.journal.empty')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {data && data.total > 50 && (
            <Group justify="center" p="md">
              <Pagination total={Math.ceil(data.total / 50)} value={page} onChange={setPage} />
            </Group>
          )}
        </Paper>
      )}

      <ManualEntryDrawer opened={manualOpen} onClose={() => setManualOpen(false)} />

      <Drawer
        opened={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={detail.data?.entryNumber ?? ''}
        position={i18n.language === 'ar' ? 'left' : 'right'}
        size="lg"
        padding="lg"
      >
        {detail.data && <EntryDetail entry={detail.data} lang={i18n.language} />}
      </Drawer>
    </Stack>
  );
}

function EntryDetail({ entry, lang }: { entry: JournalEntryDto; lang: string }) {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <Group gap="lg">
        <Text size="sm" c="dimmed">{entry.entryDate}</Text>
        <Badge size="xs" variant="light" radius="sm">{t(sourceLabel(entry.source))}</Badge>
        {entry.isReversal && (
          <Badge size="xs" color="orange" variant="light" radius="sm">
            {t('accounting.journal.reversal')}
          </Badge>
        )}
      </Group>
      {entry.description && <Text size="sm">{entry.description}</Text>}
      <Table verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('accounting.journal.columns.account')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.debit')}</Table.Th>
            <Table.Th style={{ textAlign: 'end' }}>{t('accounting.journal.columns.credit')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(entry.lines ?? []).map((l) => (
            <Table.Tr key={l.journalEntryLineId}>
              <Table.Td>
                <Text size="sm">
                  <span className="font-numeric">{l.accountCode}</span>{' '}
                  {lang === 'ar' ? l.accountNameAr : (l.accountNameEn ?? l.accountNameAr)}
                </Text>
                {l.memo && <Text size="xs" c="dimmed">{l.memo}</Text>}
              </Table.Td>
              <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                {l.debit > 0 ? l.debit.toFixed(2) : ''}
              </Table.Td>
              <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                {l.credit > 0 ? l.credit.toFixed(2) : ''}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="flex-end" gap="lg">
        <Text size="sm" fw={700} className="font-numeric">
          {entry.totalDebit.toFixed(2)} / {entry.totalCredit.toFixed(2)}
        </Text>
      </Group>
    </Stack>
  );
}

function sourceLabel(source: number): string {
  switch (source) {
    case JOURNAL_SOURCE.MANUAL: return 'accounting.journal.source.manual';
    case JOURNAL_SOURCE.POS_SALE: return 'accounting.journal.source.posSale';
    case JOURNAL_SOURCE.PURCHASE_INVOICE: return 'accounting.journal.source.purchaseInvoice';
    case JOURNAL_SOURCE.CHEQUE: return 'accounting.journal.source.cheque';
    case JOURNAL_SOURCE.EXPENSE: return 'accounting.journal.source.expense';
    case JOURNAL_SOURCE.ADJUSTMENT: return 'accounting.journal.source.adjustment';
    case JOURNAL_SOURCE.GOODS_RECEIPT: return 'accounting.journal.source.goodsReceipt';
    default: return 'accounting.journal.source.manual';
  }
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
